import { ReactNode, useDeferredValue } from 'react';
import { cn } from '../../utils/utils';
import { useSlidingPill } from '../../hooks/useSlidingPill';
import { useTabEnter } from '../../hooks/useTabEnter';

export interface Tab {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  count?: number;
  disabled?: boolean;
  content: ReactNode;
}

export interface TabbedInterfaceProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  variant?: 'default' | 'pills' | 'underline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  tabsClassName?: string;
  contentClassName?: string;
  showCounts?: boolean;
  rightElement?: ReactNode;
}

const variantClasses = {
  default: {
    container: 'flex rounded-lg p-1.5 gap-1',
    // `tap-target`: drawn 40px tall, tapped as 44px on a phone.
    tab: 'tap-target relative z-10 flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md transition-colors duration-200',
    active: 'text-foreground',
    pill: 'rounded-md bg-secondary shadow-sm',
    inactive:
      'text-muted-foreground hover:bg-muted dark:hover:bg-accent hover:text-foreground',
  },
  pills: {
    // Scrolls sideways rather than wrapping or clipping: four pills (the
    // Appendix) are wider than a phone. The scrollbar is hidden; the cut-off
    // last pill is the cue that there is more.
    container:
      'flex gap-2 px-3 py-2 sm:px-4 sm:py-3 border-b border-border overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
    // `tap-target`: drawn 36px tall, tapped as 44px on a phone.
    tab: 'tap-target relative z-10 flex flex-shrink-0 items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium rounded-full transition-colors duration-200',
    active: 'text-primary-foreground',
    pill: 'rounded-full bg-primary shadow-sm',
    inactive: 'text-muted-foreground hover:text-foreground hover:bg-muted',
  },
  underline: {
    container: 'flex border-b',
    tab: 'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 border-transparent transition-all duration-200',
    active: 'border-indigo-500 dark:border-primary',
    inactive: 'hover:border-gray-300',
    /** No pill: the underline is the active marker. */
    pill: null,
  },
};

const sizeClasses = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
};

export function TabbedInterface({
  tabs,
  activeTab,
  onTabChange,
  variant = 'default',
  size = 'md',
  className,
  tabsClassName,
  contentClassName,
  showCounts = true,
  rightElement,
}: TabbedInterfaceProps) {
  const variantConfig: {
    container: string;
    tab: string;
    active: string;
    inactive: string;
    pill: string | null;
  } = variantClasses[variant];
  // The bar follows `activeTab` at once; the panel follows this deferred copy,
  // so React commits the cheap bar change (and its sliding pill) before
  // mounting the new panel. Otherwise a heavy panel (a quiz runner) held the
  // pill still for ~200ms and the slide was half skipped.
  const shownTab = useDeferredValue(activeTab);
  const activeTabData = tabs.find(tab => tab.id === shownTab);
  // The active fill glides between tabs (`pills` and `default`), and the
  // incoming panel rises in — the same motion as the lesson's own tab bars.
  const { containerRef, pill } = useSlidingPill(
    activeTab,
    variantConfig.pill ?? ''
  );
  const contentRef = useTabEnter(shownTab);

  return (
    <div className={cn('flex h-full flex-col', className)}>
      {/* Tab Navigation */}
      <div
        className={cn(
          variantConfig.container,
          variant === 'underline' ? 'border-border' : '',
          sizeClasses[size],
          rightElement ? 'justify-between' : '',
          tabsClassName
        )}
      >
        <div
          ref={containerRef}
          // `w-max` for pills so the row can be wider than its scroller;
          // the stretched variants share the width with `flex-1` tabs.
          className={cn(
            'relative flex gap-2',
            variant === 'pills' ? 'w-max min-w-full' : 'w-full'
          )}
        >
          {variantConfig.pill && pill}
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const isDisabled = tab.disabled;

            return (
              <button
                key={tab.id}
                onClick={() => !isDisabled && onTabChange(tab.id)}
                disabled={isDisabled}
                data-pill-active={
                  isActive && variantConfig.pill ? 'true' : undefined
                }
                className={cn(
                  variantConfig.tab,
                  isActive
                    ? variantConfig.active
                    : isDisabled
                      ? `${'text-muted-foreground'} cursor-not-allowed`
                      : variantConfig.inactive,
                  variant === 'underline' && isActive
                    ? variantConfig.active
                    : ''
                )}
              >
                {Icon && <Icon className='h-4 w-4' />}
                <span>{tab.label}</span>

                {showCounts && tab.count !== undefined && tab.count > 0 && (
                  <span
                    className={cn(
                      'ml-1 rounded-full px-1.5 py-0.5 text-xs font-medium',
                      variant === 'pills' && isActive
                        ? 'bg-white/20 text-white dark:bg-gray-900/20'
                        : isActive
                          ? // `bg-brand-500`, not `bg-indigo-500`. Same colour,
                            // but through the brand ramp rather than Tailwind's
                            // stock palette — the raw class was a second accent
                            // competing with the token that already existed, and
                            // it sat on the same element as a `dark:` token
                            // override. Deliberately NOT `StatusBadge`: that is
                            // a pale /10 tint for status, this is a solid fill
                            // marking the active tab. Same box, different job.
                            'dark:bg-primary dark:text-primary-foreground bg-brand-500 text-white'
                          : 'bg-secondary text-muted-foreground'
                    )}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {rightElement && (
          <div className='flex items-center'>{rightElement}</div>
        )}
      </div>

      {/* Tab Content */}
      <div
        ref={contentRef}
        className={cn('flex-1 overflow-hidden', contentClassName)}
      >
        {activeTabData?.content}
      </div>
    </div>
  );
}
